import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PopoverPortal } from '@radix-ui/react-popover';

import {
    Form,
    FormControl,
    FormControlStack,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from 'src/registry/ui/Form';
import TextareaFormField from 'src/registry/components/forms/TextareaFormField';
import { Button } from 'src/registry/ui/Button';
import { Calendar } from 'src/registry/ui/Calendar';
import { Input } from 'src/registry/ui/Input';
import { Popover, PopoverContent, PopoverTrigger } from 'src/registry/ui/Popover';
import { cn } from 'src/utils';
import { ICON_MAP } from 'src/utils/constants';
import { generalMessages } from 'src/utils/messages';
import { ReferenceRateValueFormSchemaValues } from 'src/features/referenceRateValues/utils/formSchema';
import { referenceRateValuesMessages } from 'src/features/referenceRateValues/utils/messages';

type ReferenceRateValueFormProps = {
    form: UseFormReturn<ReferenceRateValueFormSchemaValues>;
    onSubmit: (values: ReferenceRateValueFormSchemaValues) => void;
};

function ReferenceRateValueForm({ onSubmit, form }: ReferenceRateValueFormProps) {
    const { formatMessage, formatDate } = useIntl();
    const [displayAmount, setDisplayAmount] = React.useState('');
    const [openValidFromCalendar, setOpenValidFromCalendar] = React.useState<boolean>(false);

    const { amount } = form.watch();

    React.useEffect(() => {
        if (amount != null) {
            setDisplayAmount(String(amount / 100));
        } else {
            setDisplayAmount('');
        }
    }, [amount]);

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <FormField
                control={form.control}
                name="valid_from"
                render={({ field }) => {
                    const selectedDate = field.value ? new Date(field.value) : undefined;

                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog" required>
                                {formatMessage(generalMessages.validFrom)}
                            </FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <Popover open={openValidFromCalendar} onOpenChange={setOpenValidFromCalendar}>
                                        <PopoverTrigger asChild>
                                            <Button
                                                id="valid_from"
                                                variant="outline"
                                                className="w-full justify-start text-base font-normal"
                                                onClick={() => setOpenValidFromCalendar(true)}
                                            >
                                                <FontAwesomeIcon
                                                    icon={ICON_MAP.CALENDAR}
                                                    className="text-primary-700 mr-1 size-4"
                                                />
                                                <span className={cn(!field.value && 'text-gray-500')}>
                                                    {field.value
                                                        ? formatDate(new Date(field.value))
                                                        : `${formatMessage(generalMessages.selectDate)}...`}
                                                </span>
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverPortal>
                                            <PopoverContent className="z-50 bg-white p-4 shadow-sm" align="end">
                                                <Calendar
                                                    mode="single"
                                                    selected={selectedDate}
                                                    onSelect={(date) => {
                                                        if (date) {
                                                            field.onChange(dayjs(date).format('YYYY-MM-DD'));
                                                            setOpenValidFromCalendar(false);
                                                        }
                                                    }}
                                                />
                                            </PopoverContent>
                                        </PopoverPortal>
                                    </Popover>
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    );
                }}
            />
            {/* There is deliberately no end date field - see the form schema for why. */}
            <FormDescription>{formatMessage(referenceRateValuesMessages.contiguityHint)}</FormDescription>
            <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...referenceRateValuesMessages.amount} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder={`${formatMessage(referenceRateValuesMessages.amount)}...`}
                                    value={displayAmount}
                                    onBlur={() => {
                                        if (!displayAmount) return;

                                        const numeric = parseFloat(displayAmount);
                                        if (!isNaN(numeric)) {
                                            setDisplayAmount(numeric.toFixed(2));
                                        }
                                    }}
                                    onChange={(e) => {
                                        const inputValue = e.target.value;

                                        if (!/^\d*\.?\d{0,2}$/.test(inputValue)) return;

                                        setDisplayAmount(inputValue);

                                        const numeric = parseFloat(inputValue);
                                        if (!isNaN(numeric)) {
                                            field.onChange(Math.round(numeric * 100));
                                        } else {
                                            field.onChange(undefined);
                                            setDisplayAmount('');
                                        }
                                    }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <TextareaFormField<ReferenceRateValueFormSchemaValues>
                form={form}
                name="source_note"
                required={false}
                label={formatMessage(referenceRateValuesMessages.sourceNote)}
            />
        </Form>
    );
}

export default ReferenceRateValueForm;
